import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api, formatApiError } from "@/lib/api";
import { eur, dateIt } from "@/lib/format";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import ModalityBadge from "@/components/app/ModalityBadge";
import { EditionSelect, upcomingEditions } from "@/components/app/EditionSelect";
import { toast } from "sonner";
import { ArrowLeft, UserPlus, Send, Lock, CheckCircle2, MapPin, Loader2 } from "lucide-react";

function InfoBox({ label, value }) {
  return (
    <div className="p-4 bg-white rounded-xl border border-slate-200">
      <div className="text-xs uppercase text-amber-600 font-bold tracking-widest">{label}</div>
      <div className="font-display font-bold text-slate-900 mt-1 text-sm">{value}</div>
    </div>
  );
}

function Editions({ editions }) {
  if (!editions.length) return null;
  return (
    <Card className="border-slate-200"><CardContent className="p-6">
      <h2 className="font-display text-xl font-bold mb-4">Prossime edizioni</h2>
      <div className="space-y-3">
        {editions.map((e) => (
          <div key={e.id} className="flex items-center gap-4 p-3 border border-slate-100 rounded-lg" data-testid={`cliente-edition-${e.id}`}>
            <div className="text-center bg-amber-50 rounded-lg p-2 min-w-[56px]">
              <div className="text-xs uppercase text-amber-700 font-bold">{new Date(e.start_date).toLocaleDateString("it-IT", { month: "short" })}</div>
              <div className="font-display text-xl font-extrabold text-slate-900">{new Date(e.start_date).getDate()}</div>
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-slate-900">{dateIt(e.start_date)} · {e.start_time}</div>
              <div className="text-xs text-slate-500 flex items-center gap-1 flex-wrap"><MapPin className="w-3 h-3" />{e.location || "-"} · {e.seats_available > 0 ? `${e.seats_available} posti` : "Completa"}</div>
            </div>
          </div>
        ))}
      </div>
    </CardContent></Card>
  );
}

function ActionCard({ course, onRequested }) {
  const navigate = useNavigate();
  const [editionId, setEditionId] = useState("");
  const [busy, setBusy] = useState(false);
  const isVideo = course.modality === "videoconferenza";
  const editions = upcomingEditions(course.editions);
  const enroll = () => {
    if (isVideo && !editionId) return toast.error("Seleziona l'edizione in programma");
    navigate(`/cliente/iscrizioni?corso=${course.course_id}${editionId ? `&edizione=${editionId}` : ""}`);
  };
  const request = async () => {
    setBusy(true);
    try { await api.post("/cliente/activation-requests", { course_id: course.course_id }); onRequested(); toast.success("Richiesta di attivazione inviata"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  if (!course.available) {
    return (
      <Card className="border-slate-200 athena-shadow sticky top-6"><CardContent className="p-6 space-y-4">
        <span className="inline-flex items-center gap-1 rounded-full bg-red-600 text-white text-xs font-bold px-3 py-1" data-testid="cliente-detail-inactive"><Lock className="w-3 h-3" /> Corso non ancora attivato</span>
        {course.request_status ? (
          <Badge className="bg-sky-100 text-sky-800 hover:bg-sky-100 w-full justify-center h-10" data-testid="cliente-detail-request-sent"><CheckCircle2 className="w-4 h-4 mr-1" /> Richiesta inviata</Badge>
        ) : (
          <Button onClick={request} disabled={busy} className="w-full h-11 bg-[#0F2942] hover:bg-[#1B3B5F]" data-testid="cliente-detail-request">
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />} Richiedi attivazione
          </Button>
        )}
      </CardContent></Card>
    );
  }
  return (
    <Card className="border-slate-200 athena-shadow sticky top-6"><CardContent className="p-6 space-y-4">
      <div>
        <div className="text-xs uppercase text-amber-600 font-bold tracking-widest">Il tuo prezzo riservato</div>
        <div className="font-display text-4xl font-extrabold text-[#0F2942] mt-1" data-testid="cliente-detail-price">{typeof course.price === "number" ? eur(course.price) : "—"}</div>
        <div className="text-sm text-slate-500">{Number(course.vat_rate) === 0 ? "Esente IVA" : `+ IVA ${course.vat_rate ?? 22}%`}</div>
      </div>
      {isVideo && <EditionSelect editions={editions} value={editionId} onChange={setEditionId} />}
      <Button onClick={enroll} disabled={isVideo && editions.length === 0} className="w-full h-11 bg-amber-500 hover:bg-amber-600 text-white" data-testid="cliente-detail-enroll">
        <UserPlus className="w-4 h-4 mr-2" /> Iscrivi un corsista
      </Button>
    </CardContent></Card>
  );
}

export default function ClienteCourseDetail() {
  const { user } = useAuth();
  const { id } = useParams();
  const [course, setCourse] = useState(null);
  const [error, setError] = useState(false);
  useEffect(() => { api.get(`/cliente/courses/${id}`).then((r) => setCourse(r.data)).catch(() => setError(true)); }, [id]);
  if (user?.client?.billing_mode === "slot") return <Navigate to="/cliente" replace />;
  if (error) return <div className="text-slate-500" data-testid="cliente-detail-error">Corso non disponibile.</div>;
  if (!course) return <div className="text-slate-500">Caricamento…</div>;
  return (
    <div className="space-y-6" data-testid="cliente-course-detail">
      <Link to="/cliente/catalogo" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900" data-testid="cliente-detail-back"><ArrowLeft className="w-4 h-4" /> Torna al catalogo</Link>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="aspect-[16/8] rounded-2xl overflow-hidden bg-slate-100">
            {course.image_url && <img src={course.image_url} alt={course.title} className={`w-full h-full object-cover ${course.available ? "" : "grayscale"}`} />}
          </div>
          <div className="flex gap-2">
            <Badge className="bg-white text-slate-700 border border-slate-200">{course.category}</Badge>
            <ModalityBadge modality={course.modality} testid="cliente-detail-modality" />
          </div>
          <h1 className="font-display text-3xl lg:text-4xl font-extrabold text-slate-900" data-testid="cliente-detail-title">{course.title}</h1>
          <p className="text-slate-600 leading-relaxed whitespace-pre-line">{course.description}</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <InfoBox label="Durata" value={course.duration_hours ? `${course.duration_hours} ore` : "-"} />
            <InfoBox label="Modalità" value={<span className="capitalize">{course.modality}</span>} />
            <InfoBox label="Destinatari" value={course.audience || "Aziende"} />
            <InfoBox label="Validità" value={course.validity || "-"} />
          </div>
          <Editions editions={upcomingEditions(course.editions)} />
        </div>
        <div><ActionCard course={course} onRequested={() => setCourse((c) => ({ ...c, request_status: "nuova" }))} /></div>
      </div>
    </div>
  );
}
